import { useState, useMemo, useCallback } from "react";
import {
  Box,
  Heading,
  VStack,
  HStack,
  Text,
  Button,
  Select,
  FormControl,
  FormLabel,
  Card,
  CardBody,
  Stack,
  Input,
  InputGroup,
  InputRightElement,
  useToast,
  Spacer,
  Checkbox,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { createColumnHelper } from "@tanstack/react-table";
import { UserId, User } from "features/User/models";
import { FloatingField } from "design-system";
import { API_ROUTES } from "definitions/constants/routeConstants";
import ControlledDataTable from "components/Table/ControlledDataTable";
import ButtonWithConfirm from "components/Button/ButtonWithConfirm";
import { Text12 } from "components/Text";
import { useQueryClient } from "@tanstack/react-query";
import useModal from "hooks/useModal";
import {
  useCalendarsQuery,
  useAddCalendarPermission,
  useUpdateCalendarPermission,
  useRemoveCalendarPermission,
  useCreateCalendar,
  CalendarPermission,
  useCalendarPermissionsQuery,
  calendarKeys,
} from "./hooks";

const columnHelper = createColumnHelper<CalendarPermission>();

const roleOptions = [
  { value: "reader", label: "Reader" },
  { value: "writer", label: "Writer" },
];

function NewCalendarModalBody({ onComplete }: { onComplete: () => void }) {
  const [calendarUrl, setCalendarUrl] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const createCalendarMutation = useCreateCalendar();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    createCalendarMutation.mutate(
      { calendarUrl, default: isDefault },
      {
        onSuccess: () => {
          onComplete();
        },
      },
    );
  };

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch">
        <FormControl isRequired>
          <FormLabel>Calendar URL</FormLabel>
          <Input
            placeholder="Paste your Google Calendar URL here"
            value={calendarUrl}
            onChange={(e) => setCalendarUrl(e.target.value)}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
          <Text fontSize="sm" color="fg.muted" mt={2}>
            Copy the URL from your Google Calendar and paste it here to import the calendar.
          </Text>
        </FormControl>
        <FormControl>
          <Checkbox isChecked={isDefault} onChange={(e) => setIsDefault(e.target.checked)}>
            Set as default calendar
          </Checkbox>
          <Text fontSize="sm" color="fg.muted" mt={1}>
            The default calendar is used for scheduling new meetings and events.
          </Text>
        </FormControl>
        <HStack justify="flex-end" spacing={3}>
          <Button variant="outline" onClick={onComplete}>
            Cancel
          </Button>
          <Button type="submit" isLoading={createCalendarMutation.isPending}>
            Add Calendar
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
}

function CalendarSettingsHeader() {
  const {
    open: openNewCalendarModal,
    close: closeNewCalendarModal,
    Modal: NewCalendarModal,
  } = useModal({
    headerTitle: "Add Existing Calendar",
    size: "xl",
  });

  return (
    <>
      <HStack w="100%">
        <Heading size="sm" color="fg.subtle" mb={4}>
          Calendar Settings
        </Heading>
        <Spacer />
        <Button onClick={openNewCalendarModal}>Add Existing Calendar</Button>
      </HStack>
      <NewCalendarModal>
        <NewCalendarModalBody onComplete={closeNewCalendarModal} />
      </NewCalendarModal>
    </>
  );
}

export default function CalendarPermissionsSettings() {
  const [selectedCalendarId, setSelectedCalendarId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<{ userId: UserId; role: string }>({
    defaultValues: { role: "writer" },
  });

  const { data: calendars, isLoading: calendarsLoading } = useCalendarsQuery();
  const selectedCalendar = useMemo(() => {
    if (!calendars || calendars.length === 0) return null;
    return (
      calendars.find((cal) => cal.id === selectedCalendarId) || calendars.find((cal) => cal.default) || calendars[0]
    );
  }, [calendars, selectedCalendarId]);

  const addPermissionMutation = useAddCalendarPermission();
  const updatePermissionMutation = useUpdateCalendarPermission();
  const removePermissionMutation = useRemoveCalendarPermission();
  const [mutatingPermissionId, setMutatingPermissionId] = useState<string>();
  const toast = useToast();

  const onPermissionMutationSuccess = useCallback(() => {
    setMutatingPermissionId(undefined);
    if (selectedCalendar) {
      queryClient.invalidateQueries({ queryKey: calendarKeys.permissions(selectedCalendar.id) });
    }
  }, [selectedCalendar, queryClient]);

  const handleAddPermission = (data: { userId: UserId; role: string }) => {
    if (selectedCalendar && data.userId) {
      addPermissionMutation.mutate(
        {
          calendarId: selectedCalendar.id,
          userId: data.userId,
          role: data.role,
        },
        { onSuccess: onPermissionMutationSuccess },
      );
      reset();
    }
  };

  const handleUpdatePermission = useCallback(
    (permission: CalendarPermission, newRole: string) => {
      if (selectedCalendar) {
        setMutatingPermissionId(permission.id);
        updatePermissionMutation.mutate(
          {
            calendarId: selectedCalendar.id,
            permissionId: permission.id,
            role: newRole,
          },
          { onSuccess: onPermissionMutationSuccess },
        );
      }
    },
    [selectedCalendar, updatePermissionMutation, onPermissionMutationSuccess],
  );

  const handleRemovePermission = useCallback(
    (permission: CalendarPermission) => {
      if (selectedCalendar) {
        setMutatingPermissionId(permission.id);
        removePermissionMutation.mutate(
          {
            calendarId: selectedCalendar.id,
            permissionId: permission.id,
          },
          { onSuccess: onPermissionMutationSuccess },
        );
      }
    },
    [selectedCalendar, removePermissionMutation, onPermissionMutationSuccess],
  );

  const handleCopySubscriptionUrl = async () => {
    if (selectedCalendar?.subscriptionUrl) {
      try {
        await navigator.clipboard.writeText(selectedCalendar.subscriptionUrl);
        toast({
          title: "Copied!",
          description: "Calendar subscription URL copied to clipboard",
          status: "success",
          duration: 2000,
        });
      } catch {
        toast({
          title: "Copy failed",
          description: "Failed to copy URL to clipboard",
          status: "error",
          duration: 3000,
        });
      }
    }
  };

  const { data: calendarPermissions = [], isLoading: calendarPermissionsLoading } = useCalendarPermissionsQuery(
    selectedCalendar?.id || "",
    { enabled: !!selectedCalendar },
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("user", {
        header: "User",
        cell: ({ row }) => <Text12 wordBreak="break-word">{row.original.user.fullName}</Text12>,
      }),
      columnHelper.accessor("user.workEmail", {
        header: "Email",
        cell: ({ row }) => <Text12 wordBreak="break-word">{row.original.user.workEmail}</Text12>,
      }),
      columnHelper.accessor("role", {
        header: "Role",
        cell: ({ row }) => {
          const permission = row.original;
          if (permission.role === "owner") {
            return (
              <Text12 fontWeight="bold" color="blue.500">
                Owner
              </Text12>
            );
          }
          return (
            <Select
              value={permission.role}
              onChange={(e) => handleUpdatePermission(permission, e.target.value)}
              size="sm"
            >
              <option value="reader">Reader</option>
              <option value="writer">Writer</option>
            </Select>
          );
        },
      }),
      columnHelper.accessor((row) => row, {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const permission = row.original;
          if (permission.role === "owner") {
            return null;
          }
          return (
            <ButtonWithConfirm
              buttonText="Remove"
              header="Remove Calendar Permission"
              body={`Are you sure you want to remove calendar access for ${permission.user.fullName}?\n\nThis action cannot be undone.`}
              confirmButtonText="Remove"
              handleConfirm={() => handleRemovePermission(permission)}
              buttonProps={{
                size: "xs",
                colorScheme: "red",
                variant: "outline",
                isLoading:
                  (removePermissionMutation.isPending || updatePermissionMutation.isPending) &&
                  mutatingPermissionId === permission.id,
              }}
            />
          );
        },
      }),
    ],
    [
      handleUpdatePermission,
      handleRemovePermission,
      mutatingPermissionId,
      removePermissionMutation.isPending,
      updatePermissionMutation.isPending,
    ],
  );

  if (calendarsLoading) {
    return (
      <Card>
        <CardBody>
          <Text>Loading calendars...</Text>
        </CardBody>
      </Card>
    );
  }

  if (!calendars || calendars.length === 0) {
    return (
      <Card>
        <CardBody>
          <CalendarSettingsHeader />
          <Text>No calendars found. Please connect a calendar integration first.</Text>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardBody>
        <CalendarSettingsHeader />
        <VStack spacing={6} align="stretch">
          <Box>
            <FormControl mb={4}>
              <FormLabel>Calendar</FormLabel>
              <Select value={selectedCalendar?.id || ""} onChange={(e) => setSelectedCalendarId(e.target.value)}>
                {calendars.map((calendar) => (
                  <option key={calendar.id} value={calendar.id}>
                    {calendar.name} {calendar.default && "(Default)"}
                  </option>
                ))}
              </Select>
            </FormControl>

            {selectedCalendar && (
              <Box>
                <Heading size="sm" mb={4}>
                  Calendar Permissions
                </Heading>
                <Text mb={4} color="fg.subtle">
                  Manage who can view and edit your {selectedCalendar.name} calendar.
                </Text>

                <Box as="form" onSubmit={handleSubmit(handleAddPermission)} mb={6}>
                  <Stack direction={{ base: "column", md: "row" }} spacing={4} align="center">
                    <Box flex={1}>
                      <FloatingField name="userId" errors={errors.userId?.message}>
                        <FloatingField.Label>Team Member</FloatingField.Label>
                        <FloatingField.SingleSelect<User>
                          control={control}
                          name="userId"
                          fetchPath={API_ROUTES.users.search}
                          perPagePagination={300}
                          embedOption={(user) => ({
                            value: user.id,
                            label: user.fullName ?? "",
                            rawValue: user,
                          })}
                          extractValue={(user) => user.id}
                          excludedIds={calendarPermissions.map((p) => p.user.id)}
                        />
                      </FloatingField>
                    </Box>
                    <Box minW="150px">
                      <FloatingField name="role" errors={errors.role?.message}>
                        <FloatingField.Label>Role</FloatingField.Label>
                        <FloatingField.StaticSelect
                          control={control}
                          name="role"
                          options={roleOptions}
                          isClearable={false}
                        />
                      </FloatingField>
                    </Box>
                    <Box>
                      <Button type="submit" isLoading={addPermissionMutation.isPending} size="xs">
                        Add
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <ControlledDataTable<CalendarPermission>
                  data={calendarPermissions}
                  columns={columns}
                  isLoading={calendarPermissionsLoading}
                  emptyStateComponent={<Text>No permissions found.</Text>}
                />

                <Box mt={8}>
                  <Heading size="sm" mb={4}>
                    Calendar Subscription
                  </Heading>
                  <Text mb={4} color="fg.subtle">
                    Share this calendar with others by giving them the subscription URL. They can add it to their Google
                    Calendar.
                  </Text>

                  <FormControl>
                    <FormLabel>Subscription URL</FormLabel>
                    <InputGroup>
                      <Input value={selectedCalendar.subscriptionUrl} isReadOnly pr="4.5rem" fontSize="sm" />
                      <InputRightElement width="4.5rem">
                        <Button h="1.75rem" size="sm" onClick={handleCopySubscriptionUrl}>
                          Copy
                        </Button>
                      </InputRightElement>
                    </InputGroup>
                    <Text fontSize="xs" color="fg.muted" mt={2}>
                      Others can use this URL to subscribe to this calendar in Google Calendar.
                    </Text>
                  </FormControl>
                </Box>
              </Box>
            )}
          </Box>
        </VStack>
      </CardBody>
    </Card>
  );
}
